import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { Loader2, Printer, Download, CreditCard, FileText, User, Building, PieChart, Clock, ShieldAlert, History, RotateCcw, DollarSign, CheckCircle2, CalendarClock, ArrowLeft } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { useBreadcrumbPageTitle } from "@/components/layout/DashboardBreadcrumbContext";
import { ReportOverviewMetrics } from '../components/ReportOverviewMetrics'
import { gatewayMetaLookup, getChargebackApplicationInfo, getGatewayStatuses, getPaymentLogDetail, refreshPaymentGatewayStatus } from '../services/reportsService'
import type {
  ChargebackApplicationInfo,
  GatewayStatusMetaMap,
  PaymentAndAdjustmentRow,
  PaymentLogDetailResponse,
} from '../types/api';
import type { PaymentLogPartialPayment } from '@/types/paymentLog';
import { buildPaymentLogExportUrls } from "../utils/paymentLogExportUrls";
import { isBalanceReportPaymentLogNav, resolvePaymentLogBackTarget } from "../utils/paymentLogBackToList";
import {
  buildPaidByAddressLines,
  formatPaymentLogApplicantIdDisplay,
  paymentInvoiceForLabel,
  resolvePaymentLogApplicantId,
} from "../utils/paymentLogDetailDisplay";
import { SectionInfoIcon } from "@/components/ui/SectionInfoIcon";
import { formatDateTime } from "../../application/utils/applicationFormatters";
import { formatAmount } from "@/lib/utils/formatters";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { showApiError, showSuccess } from "@/lib/utils/notifications";
import { useUserRole } from "@/hooks/useUserRole";
import {
  canShowPaymentCheckForm,
  PaymentLogAchCheckPaymentForm,
  PaymentLogActionForms,
} from "../components/PaymentLogActionForms";
import type { PaymentLogDetailProps } from '../types/pages'
import { CHARGEBACK_PAYMENT_TYPES } from '../constants/chargebackDetailConstants'
import styles from "../styles/paymentLogDetail.module.css";

function display(value: unknown): string {
  if (value == null || value === '') return '-'
  return String(value)
}

function toNumber(value: unknown): number {
  const n = Number(String(value ?? 0).replace(/[^0-9.-]+/g, ''))
  return Number.isFinite(n) ? n : 0
}

function isRefundPaymentType(value: unknown): boolean {
  const type = String(value ?? '')
  return type.includes('REFUND') || type === 'DEFAULT'
}

function isChargebackPaymentType(value: unknown): boolean {
  return CHARGEBACK_PAYMENT_TYPES.includes(String(value ?? ''))
}

function shouldLoadApplicationPaymentContext(value: unknown): boolean {
  const type = String(value ?? '')
  return (
    isChargebackPaymentType(type) ||
    type === 'BALANCE_ADJUSTMENT' ||
    type.includes('REFUND')
  )
}

/** Angular binds these address blocks with [innerHtml]; `<br>` is the line separator. */
function htmlBlockToLines(value: unknown): string[] {
  if (value == null || value === '') return []
  return String(value)
    .replace(/<br\s*\/?>/gi, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
}

function formatDetailDateTime(value: unknown): string {
  if (value == null || value === '') return '-'
  if (typeof value === 'number') return formatDateTime(value) || '-'
  const raw = String(value)
  const numeric = Number(raw)
  if (Number.isFinite(numeric) && /^\d{10}$/.test(raw)) {
    return formatDateTime(numeric) || '-'
  }
  return formatDateTime(raw) || raw
}

/** Split formatted datetime into date + time for metric cards (MRG-style layout). */
function formatDetailDateParts(value: unknown): { date: string; time?: string } {
  const full = formatDetailDateTime(value)
  if (full === '-') return { date: '-' }
  const match = full.match(/^(\d{1,2}\/\d{1,2}\/\d{4})\s+(.+)$/)
  if (match) return { date: match[1], time: match[2] }
  return { date: full }
}

function resolveLogStatus(
  logData: PaymentLogDetailResponse | null | undefined,
): string | undefined {
  if (!logData) return undefined
  return (
    logData.payment_status ||
    logData.chargeback_status ||
    logData.chargeback_type ||
    logData.payment_adjustment?.adjustment_subtype ||
    logData.adjustment_subtype ||
    ((logData as Record<string, unknown>).status as string | undefined)
  )
}

function titleCaseStatus(value: unknown): string {
  const status = display(value)
  if (status === '-' || status === 'ACH_Check_Pending') return status
  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function paymentLogBaseFee(row: PaymentAndAdjustmentRow): number {
  const surcharge = toNumber(row?.payment_log_surcharge_fee)
  const amount = toNumber(row?.payment_amount)
  const refund = toNumber(row?.payment_log_refund_fee)
  if (surcharge && surcharge < amount) return amount - surcharge
  return refund ? amount + refund * -1 : amount
}

function paymentLogServiceFee(row: PaymentAndAdjustmentRow): number {
  const surcharge = toNumber(row?.payment_log_surcharge_fee)
  const refund = toNumber(row?.payment_log_refund_fee)
  return surcharge || refund || 0
}

/**
 * Instalments belonging to this log. Angular renders one block per entry whose
 * `payment_log_id` matches the log being viewed, and hides the generic block.
 */
function partialPaymentsForLog(
  logData: PaymentLogDetailResponse,
): PaymentLogPartialPayment[] {
  const partials = logData?.partial_payments
  if (!Array.isArray(partials) || partials.length === 0) return []
  return partials.filter(
    (partial) => String(partial?.payment_log_id) === String(logData?.payment_log_id),
  )
}

function partialPaymentRows(
  partial: PaymentLogPartialPayment,
): Array<readonly [string, unknown, boolean]> {
  // [label, value, isSubRow] — sub-rows sit under the Previous/This Payment headings.
  return [
    ['Certificate Fee', partial?.access_fee ?? 0, false],
    ['Previous Access Fee', partial?.previous_principle ?? 0, true],
    ['Previous Service Fee', partial?.previous_surcharge ?? 0, true],
    ['Access Fee', partial?.payment_amount ?? 0, true],
    ['Service Fee', partial?.surcharge_fee ?? 0, true],
    ['Sales Tax', partial?.sales_tax ?? 0, true],
    ['Total', partial?.total_amount ?? 0, true],
    ['Balance Due', partial?.balance_due ?? 0, false],
  ]
}

function paymentDetailRows(logData: PaymentLogDetailResponse) {
  const adjustment = logData?.adjustment_data
  if (
    logData?.payment_type?.includes?.('CONTRACT_REFUND') &&
    adjustment &&
    typeof adjustment === 'object'
  ) {
    const rows: Array<readonly [string, unknown]> = [
      ['Certificate Fee', adjustment.certificate_fee],
      ['Starting Balance', adjustment.startBalance],
      ['Refund Fee', adjustment.refundFee],
    ]
    if (logData?.refund_processing_fee === '1' && logData?.surcharge_fee) {
      rows.push(['Refund Processing Fee', logData.surcharge_fee])
    }
    rows.push(
      ['Adjustment', adjustment.adjustmentPayment],
      ['Account Balance', adjustment.totalBalance],
      ['Refund Amount', adjustment.refundAmount],
      ['Balance Owed', adjustment.balanaceOwnPayment ?? adjustment.balanceOwnPayment],
    )
    return rows
  }

  const paymentAmount =
    logData?.payment_type === 'BALANCE_ADJUSTMENT'
      ? logData?.adjustment_amount
      : logData?.payment_amount
  const serviceCost =
    logData?.payment_type === 'DEFAULT'
      ? toNumber(logData?.payment_amount) + (toNumber(logData?.refund_fee) * -1)
      : logData?.surcharge_fee && ['CONTRACT', 'CONTRACT_INVOICE'].includes(String(logData?.payment_type))
        ? toNumber(logData?.payment_amount) - toNumber(logData?.surcharge_fee)
        : paymentAmount
  const serviceFee =
    ['CONTRACT', 'CONTRACT_INVOICE'].includes(String(logData?.payment_type)) && logData?.surcharge_fee
      ? logData.surcharge_fee
      : logData?.payment_type === 'DEFAULT' && logData?.payment_source === 'Paper'
        ? 15
        : 0
  const rows: Array<readonly [string, unknown]> = [
    [logData?.payment_type === 'DEFAULT' ? 'Net Due' : 'Service Cost', serviceCost],
    ['Payment Service Fee', serviceFee],
    ['Sales Tax', logData?.sales_tax ?? 0],
  ]
  if (logData?.refund_fee) {
    rows.push([
      logData?.payment_type === 'DEFAULT' ? 'Paper Check Fee' : 'Less Refund Fee',
      logData.refund_fee,
    ])
  }
  rows.push(['Total', paymentAmount])
  return rows
}

export function PaymentLogDetail({ source = "payment_log" }: PaymentLogDetailProps) {
  const { logId } = useParams<{ logId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLibertyRole, isCompanyRole, roleName } = useUserRole();
  useBreadcrumbPageTitle('Transaction Details')
  const navState = isBalanceReportPaymentLogNav(location.state)
    ? location.state
    : null
  const fromBalanceReport = navState != null
  const backNavLabel = navState?.returnLabel?.trim() || 'Back'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logData, setLogData] = useState<PaymentLogDetailResponse | null>(null);
  const [applicationAllData, setApplicationAllData] =
    useState<ChargebackApplicationInfo | null>(null);
  const [gatewayMeta, setGatewayMeta] = useState<GatewayStatusMetaMap | null>(null);
  const [gatewayConfirmOpen, setGatewayConfirmOpen] = useState(false);
  const [gatewayRefreshing, setGatewayRefreshing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const meta = await getGatewayStatuses();
        if (!cancelled) setGatewayMeta(meta);
      } catch {
        /* Detail page still works with raw gateway_status code */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!logId) return;

    let cancelled = false

    const fetchLog = async () => {
      try {
        setLoading(true);
        setError(null);
        setLogData(null);
        const data = await getPaymentLogDetail(logId, source);
        if (cancelled) return
        setLogData(data);
        const resolvedApplicantId = resolvePaymentLogApplicantId(data);
        if (
          resolvedApplicantId != null &&
          shouldLoadApplicationPaymentContext(data?.payment_type)
        ) {
          try {
            const applicationData = await getChargebackApplicationInfo(resolvedApplicantId);
            if (!cancelled) setApplicationAllData(applicationData);
          } catch {
            if (!cancelled) setApplicationAllData(null);
          }
        } else {
          setApplicationAllData(null);
        }
      } catch (err: unknown) {
        if (cancelled) return
        // Match Default Payment Log edit: API failures as Message popup (renders HTML), not inline text.
        showApiError(err, () => {
          const target = resolvePaymentLogBackTarget({
            hash: location.hash,
            applicantId: null,
            state: location.state,
          })
          if (target) {
            navigate(target)
          } else if (location.pathname.includes('/default-payment-logs/')) {
            navigate('/dashboard/reports/default-payment-logs')
          } else if (location.pathname.includes('/payment-logs/')) {
            navigate('/dashboard/reports/payment-logs')
          } else {
            navigate(-1)
          }
        })
        setError('Failed to load payment log')
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void fetchLog();
    return () => {
      cancelled = true
    }
  }, [logId, source, refreshKey, location.hash, location.pathname, location.state, navigate]);

  const { downloadUrl, printUrl } = useMemo(
    () => (logId ? buildPaymentLogExportUrls(logId, source) : { downloadUrl: "", printUrl: "" }),
    [logId, source],
  );

  const gatewayPaymentLogId = useMemo(() => {
    if (source === "payment_log") return logId ?? ""
    const fromResponse = logData?.payment_log_id
    return fromResponse != null ? String(fromResponse) : ""
  }, [source, logId, logData?.payment_log_id]);

  const gatewayResolved = useMemo(
    () => gatewayMetaLookup(gatewayMeta, logData?.payment_source, logData?.gateway_status),
    [gatewayMeta, logData?.payment_source, logData?.gateway_status],
  );

  const applicantId = useMemo(
    () => resolvePaymentLogApplicantId(logData),
    [logData],
  );

  const paidByLines = useMemo(() => buildPaidByAddressLines(logData), [logData]);
  const isRefund = isRefundPaymentType(logData?.payment_type)
  const amountForGross =
    logData?.payment_type === 'BALANCE_ADJUSTMENT'
      ? logData?.adjustment_amount
      : logData?.payment_amount
  const partialPayments = useMemo(
    () => (logData ? partialPaymentsForLog(logData) : []),
    [logData],
  )

  const gatewayTooltipText =
    gatewayResolved.description ||
    (logData?.gateway_status != null ? String(logData.gateway_status) : "");

  const showGatewayStatusButton =
    Boolean(gatewayPaymentLogId) &&
    String(logData?.payment_source ?? "").toUpperCase() === "CARDCONNECT";

  const paymentLogRows = useMemo(() => {
    const rows = applicationAllData?.payment_and_payment_adjustment
    if (!Array.isArray(rows)) return []
    return rows.filter((row) => {
      const type = String(row?.payment_type ?? '')
      if (
        ['APPLICATION_FEE', 'APPLICATION_FEE_REFUND'].includes(type) &&
        String(row?.fk_applicant_id ?? '') !== String(logData?.applicant_id ?? '')
      ) {
        return false
      }
      return true
    })
  }, [applicationAllData?.payment_and_payment_adjustment, logData?.applicant_id])

  const showChargebackInfo =
    isLibertyRole && isChargebackPaymentType(logData?.payment_type)
  const showInlineCheckForm =
    Boolean(logData) &&
    canShowPaymentCheckForm(roleName, String(logData?.payment_status ?? '')) &&
    !showChargebackInfo
  const showPaymentLogTable =
    isLibertyRole &&
    paymentLogRows.length > 0 &&
    shouldLoadApplicationPaymentContext(logData?.payment_type)

  const adjustmentHistory = logData?.payment_adjustment_history ?? []
  const notesHistory = logData?.payment_notes_history ?? []
  const showChargebackHistory =
    isLibertyRole &&
    ['CONTRACT_CHARGEBACK', 'CHGBACK_APP_FEE', 'CHGBACK_CONTRACT'].includes(
      String(logData?.payment_type),
    ) &&
    adjustmentHistory.length > 0
  const showBalanceAdjustmentHistory =
    isLibertyRole &&
    logData?.payment_type === 'BALANCE_ADJUSTMENT' &&
    adjustmentHistory.length > 0
  const showNotesHistory =
    isLibertyRole &&
    Boolean(logData?.is_external_payment) &&
    Boolean(logData?.payment_log_id) &&
    notesHistory.length > 0

  const backToList = () => {
    const target = resolvePaymentLogBackTarget({
      hash: location.hash,
      applicantId: logData?.applicant_id,
      state: location.state,
    })
    if (target) navigate(target)
    else navigate(-1)
  }

  const handleGatewayStatusConfirm = async () => {
    if (!gatewayPaymentLogId) return;
    setGatewayRefreshing(true);
    try {
      await refreshPaymentGatewayStatus(gatewayPaymentLogId);
      showSuccess("Gateway Status Updated Successfully");
      setGatewayConfirmOpen(false);
      backToList();
    } catch (err) {
      showApiError(err);
    } finally {
      setGatewayRefreshing(false);
    }
  };

  return (
    <div data-testid="payment-log-detail-page">
      <PageShell
        title="Transaction Details"
        pageHeaderTestIdRoot="payment-log-detail-page-header"
        contentClassName="!space-y-4 sm:!space-y-5 w-full max-w-none min-w-0 px-3 sm:px-6 lg:px-8 xl:px-10"
        showBreadcrumbs={!fromBalanceReport}
        headerAction={
          <div className={styles.topHeaderRow}>
            {fromBalanceReport ? (
              <button
                type="button"
                onClick={backToList}
                className={styles.backLink}
                data-testid="payment-log-detail-back"
              >
                <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
                {backNavLabel}
              </button>
            ) : (
              <span className="min-w-0" aria-hidden />
            )}
            <div className={styles.topHeaderActions}>
              {!loading && !error && showGatewayStatusButton ? (
                <button
                  type="button"
                  disabled={gatewayRefreshing}
                  onClick={() => setGatewayConfirmOpen(true)}
                  className={styles.actionButtonPrimary}
                >
                  {gatewayRefreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : null}
                  Gateway Status
                </button>
              ) : null}
              {downloadUrl ? (
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 sm:px-4 text-sm font-semibold text-[#1597ff] shadow-sm transition hover:bg-slate-50 whitespace-nowrap"
                  aria-label="Download payment receipt"
                  title="Download payment receipt"
                >
                  <Download className="h-4 w-4 shrink-0" aria-hidden />
                  Download
                </a>
              ) : (
                <button
                  type="button"
                  disabled
                  className="inline-flex min-h-9 cursor-not-allowed items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 sm:px-4 text-sm font-semibold text-slate-300 whitespace-nowrap"
                  aria-label="Download payment receipt unavailable"
                  title="Sign in required to download"
                >
                  <Download className="h-4 w-4 shrink-0" aria-hidden />
                  Download
                </button>
              )}
              {printUrl ? (
                <a
                  href={printUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 sm:px-4 text-sm font-semibold text-[#1597ff] shadow-sm transition hover:bg-slate-50 whitespace-nowrap"
                  aria-label="Print payment receipt"
                  title="Print payment receipt"
                >
                  <Printer className="h-4 w-4 shrink-0" aria-hidden />
                  Print
                </a>
              ) : (
                <button
                  type="button"
                  disabled
                  className="inline-flex min-h-9 cursor-not-allowed items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 sm:px-4 text-sm font-semibold text-slate-300 whitespace-nowrap"
                  aria-label="Print unavailable"
                  title="Sign in required to print"
                >
                  <Printer className="h-4 w-4 shrink-0" aria-hidden />
                  Print
                </button>
              )}
            </div>
          </div>
        }
      >
        {loading ? (
          <div className="flex min-h-[12rem] items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-[#1597ff]" aria-hidden />
          </div>
        ) : logData ? (
          <div className={styles.container}>
            <ConfirmDialog
              isOpen={gatewayConfirmOpen}
              title="Confirm"
              message="Are you sure want update payment gateway status?"
              primaryLabel="Yes"
              onPrimary={handleGatewayStatusConfirm}
              secondaryLabel="No"
              onSecondary={() => setGatewayConfirmOpen(false)}
              onDismiss={() => setGatewayConfirmOpen(false)}
              sectionedChrome
              messageAlign="center"
            />

            <ReportOverviewMetrics
              title="Transaction overview"
              subtitle="Payment summary"
              testIdPrefix="payment-log-detail"
              items={(() => {
                const paymentDate = formatDetailDateParts(logData.created_at)
                return [
                  {
                    id: 'status',
                    label: 'Status',
                    value: titleCaseStatus(resolveLogStatus(logData)),
                    icon: <CheckCircle2 aria-hidden />,
                    tone: 'green' as const,
                  },
                  {
                    id: 'gross-amount',
                    label: 'Gross Amount',
                    value: formatAmount(amountForGross),
                    icon: <DollarSign aria-hidden />,
                    tone: 'blue' as const,
                  },
                  {
                    id: 'payment-source',
                    label: 'Payment Source',
                    value: display(logData.payment_source).toUpperCase(),
                    icon: <CreditCard aria-hidden />,
                    tone: 'violet' as const,
                  },
                  {
                    id: 'payment-date',
                    label: 'Payment Date',
                    value: paymentDate.date,
                    secondaryValue: paymentDate.time,
                    icon: <CalendarClock aria-hidden />,
                    tone: 'sky' as const,
                  },
                ]
              })()}
            />

            <div className={styles.mainGrid}>
              <div className={styles.cardSectionFullHeight}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardIcon}>
                    <CreditCard className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className={styles.cardTitle}>Payment information</h3>
                </div>
                <div>
                  <InfoRow label={`Payment ${isRefund ? 'Refund' : 'Invoice'} for`} value={paymentInvoiceForLabel(logData)} />
                  {logData.payment_type === 'DEFAULT' ? (
                    <InfoRow label="Unit #" value={logData.unit_number} />
                  ) : null}
                  <InfoRow label="Payment status" value={renderStatusBadge(resolveLogStatus(logData))} />
                  <InfoRow label="Funding batch ID" value={logData.funding_batch_id} />
                  {logData.gateway_status ? (
                    <InfoRow
                      label="Gateway status"
                      value={
                        <span className="inline-flex min-w-0 items-center gap-1.5">
                          <span>{gatewayResolved.display}</span>
                          {gatewayTooltipText ? (
                            <SectionInfoIcon
                              align="start"
                              maxWidth={360}
                              iconSize={16}
                              aria-label="Gateway status details"
                              className="shrink-0"
                              clampPanelHeight={false}
                              contentClassName="px-3 py-2 text-xs text-slate-700 whitespace-pre-wrap"
                              content={gatewayTooltipText}
                            />
                          ) : null}
                        </span>
                      }
                    />
                  ) : null}
                  {['CONTRACT_CHARGEBACK', 'CHGBACK_CONTRACT'].includes(
                    String(logData.payment_type ?? ''),
                  ) ? (
                    <>
                      <InfoRow label="Case No" value={logData.case_number} />
                      <InfoRow label="Case Date" value={formatDetailDateTime(logData.case_date)} />
                      <InfoRow label="Chargeback transaction" value={logData.txn_number} />
                      <InfoRow
                        label="Received Date"
                        value={formatDetailDateTime(logData.chargeback_notification_date)}
                      />
                    </>
                  ) : null}
                  {logData.payment_type === 'DEFAULT' ? (
                    <>
                      <InfoRow label="Payee verified" value={logData.verify === '1' ? 'Yes' : 'No'} />
                      <InfoRow label="Pay to" value={logData.pay_to === 'COMPANY' ? 'Company' : 'Property'} />
                    </>
                  ) : String(logData.payment_type ?? '').includes('REFUND') ? null : (
                    <InfoRow label="IP address" value={logData.ip_address} />
                  )}
                  {logData.special_note ? (
                    <InfoRow label="Special note" value={logData.special_note} />
                  ) : null}
                </div>
              </div>

              <div className={styles.cardSectionFullHeight}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardIcon}>
                    <FileText className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className={styles.cardTitle}>Transaction information</h3>
                </div>
                <div>
                  <InfoRow
                    label="Application ID"
                    value={
                      applicantId == null ? (
                        '-'
                      ) : isCompanyRole ? (
                        <span className="font-semibold text-[#1597ff]">{formatPaymentLogApplicantIdDisplay(logData)}</span>
                      ) : (
                        <Link
                          to={`/application/${applicantId}`}
                          className="font-semibold text-[#1597ff] hover:underline"
                        >
                          {formatPaymentLogApplicantIdDisplay(logData)}
                        </Link>
                      )
                    }
                  />
                  <InfoRow label="Payment type" value={logData.payment_type} />
                  <InfoRow label="Batch ID" value={logData.batch_id} />
                  <InfoRow
                    label={['ACH', 'PAPER_CHECK'].includes(String(logData.payment_source)) ? 'Check / ACH #' : 'Transaction #'}
                    value={logData.auth_code}
                  />
                  {logData.card_number ? (
                    <InfoRow label="Card #" value={logData.card_number} />
                  ) : null}
                </div>
              </div>
            </div>

            <div className={styles.mainGrid}>
              <div className={styles.cardSection}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardIcon}>
                    <User className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className={styles.cardTitle}>Paid By</h3>
                </div>
                <div className="text-sm text-slate-800 space-y-1">
                  {logData.payment_type === 'DEFAULT' ? (
                    <MultilineValue lines={htmlBlockToLines(logData.payment_to)} />
                  ) : (
                    <MultilineValue lines={paidByLines} />
                  )}
                </div>
              </div>

              <div className={styles.cardSection}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardIcon}>
                    <Building className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className={styles.cardTitle}>Payment To</h3>
                </div>
                <div className="text-sm text-slate-800 space-y-1">
                  <MultilineValue
                    lines={htmlBlockToLines(isRefund ? logData.payer_info : logData.payment_to)}
                  />
                </div>
              </div>
            </div>

            <div
              className={
                showChargebackInfo || showInlineCheckForm ? styles.mainGrid : undefined
              }
            >
              <div className={styles.cardSectionFullHeight}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardIcon}>
                    <PieChart className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className={styles.cardTitle}>Payment Detail</h3>
                </div>
                <div className="flex-1 flex flex-col justify-between">
                  {partialPayments.length > 0 ? (
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {partialPayments.map((partial, partialIndex) => (
                        <div
                          key={`${partial.payment_log_id ?? 'partial'}-${partialIndex}`}
                          className={styles.paymentPartialList}
                        >
                          <div className={styles.paymentPartialHeader}>
                            Payment {partialIndex + 1}
                          </div>
                          {partialPaymentRows(partial).map(([label, value]) => {
                            const isTotal = label === 'Total'
                            return (
                              <div
                                key={label}
                                className={isTotal ? styles.totalBox : styles.infoRow}
                              >
                                <span className={styles.infoLabel}>{label}</span>
                                <span className={styles.infoValue}>
                                  {formatAmount(value as string | number | null | undefined)}
                                </span>
                              </div>
                            )
                          })}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div>
                      {paymentDetailRows(logData).map(([label, value], index, rows) => {
                        const isTotal = index === rows.length - 1
                        return (
                          <div
                            key={label}
                            className={isTotal ? styles.totalBox : styles.infoRow}
                          >
                            <span className={styles.infoLabel}>{label}</span>
                            <span className={styles.infoValue}>
                              {formatAmount(value as string | number | null | undefined)}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>

              {showChargebackInfo ? (
                <div className={styles.cardSectionFullHeight}>
                  <div className={styles.cardHeader}>
                    <div className={styles.cardIcon}>
                      <ShieldAlert className="h-5 w-5" aria-hidden />
                    </div>
                    <div className="flex flex-1 items-center justify-between">
                      <h3 className={styles.cardTitle}>Chargeback Information</h3>
                      <span className="inline-flex items-center rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-semibold text-sky-700 border border-sky-200">
                        Adjustment
                      </span>
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col justify-between">
                    <div className="grid gap-x-8 gap-y-1 md:grid-cols-2">
                      <InfoRow
                        label="Transaction Reason"
                        value={
                          logData.payment_type ? (
                            <span className="inline-flex items-center rounded bg-slate-100 px-2 py-0.5 font-mono text-xs font-semibold text-slate-800">
                              {display(logData.payment_type)}
                            </span>
                          ) : (
                            '—'
                          )
                        }
                      />
                      {logData.special_note ? (
                        <InfoRow label="Special Note" value={logData.special_note} />
                      ) : null}
                      <InfoRow
                        label="Application Cancelled"
                        value={
                          [
                            'CHARGEBACK_CANCEL_CERTIFICATE',
                            'CANCEL_CERTIFICATE',
                          ].includes(
                            String(
                              logData.payment_adjustment?.adjustment_subtype ??
                              logData.adjustment_subtype ??
                              '',
                            ),
                          ) ? (
                            <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-bold bg-rose-100 text-rose-700 border border-rose-200">
                              YES
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold bg-slate-100 text-slate-600">
                              NO
                            </span>
                          )
                        }
                      />
                      <InfoRow
                        label="Applicant Reason"
                        value={
                          logData.transaction_reason ? (
                            <span>{logData.transaction_reason}</span>
                          ) : (
                            <span className="text-slate-400 italic">Reason not provided</span>
                          )
                        }
                      />
                      <InfoRow
                        label="Notes"
                        value={
                          logData.transaction_notes ? (
                            <span>{logData.transaction_notes}</span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )
                        }
                      />
                      <InfoRow
                        label="Originator"
                        value={
                          logData.payment_paid_first_name || logData.payment_paid_last_name || logData.created_at ? (
                            <span className="inline-flex flex-wrap items-center justify-end gap-1.5 font-medium text-slate-800">
                              <span>
                                {`${display(logData.payment_paid_first_name)} ${display(logData.payment_paid_last_name)}`.replace(/—/g, '').trim() || '—'}
                              </span>
                              {logData.created_at ? (
                                <span className="text-xs text-slate-400 font-normal">
                                  • {formatDetailDateTime(logData.created_at)}
                                </span>
                              ) : null}
                            </span>
                          ) : (
                            '—'
                          )
                        }
                      />
                      <InfoRow
                        label="Progress"
                        value={renderStatusBadge(resolveLogStatus(logData))}
                      />
                    </div>
                  </div>
                </div>
              ) : showInlineCheckForm && logData ? (
                <PaymentLogAchCheckPaymentForm
                  logData={logData}
                  onRefresh={() => setRefreshKey((key) => key + 1)}
                />
              ) : null}
            </div>

            <PaymentLogActionForms
              logData={logData}
              onRefresh={() => setRefreshKey((key) => key + 1)}
              hideCheckForm={showInlineCheckForm}
            />

            {showPaymentLogTable ? (
              <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="flex items-center gap-2.5 border-b border-slate-100 px-5 py-3">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50 text-[#1597ff]">
                    <CreditCard className="h-4 w-4" aria-hidden />
                  </div>
                  <h2 className="text-sm font-semibold text-slate-800">Payment Log</h2>
                </div>
                <div className="overflow-x-auto p-3 sm:p-4">
                  <table className="w-full min-w-[900px] table-fixed overflow-hidden rounded-lg border border-slate-200 text-left text-xs text-slate-700">
                    <thead className="border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                      <tr>
                        <th className="px-3 py-2">Type</th>
                        <th className="px-3 py-2">Method</th>
                        <th className="px-3 py-2 text-right">Base Fee</th>
                        <th className="px-3 py-2 text-right">Service Fee</th>
                        <th className="px-3 py-2 text-right">Total</th>
                        <th className="px-3 py-2">Log ID</th>
                        <th className="px-3 py-2">Date / Time</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Gateway Status</th>
                        <th className="px-3 py-2">Paid By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {paymentLogRows.map((row, index) => {
                        const gateway = gatewayMetaLookup(
                          gatewayMeta,
                          row?.payment_source,
                          row?.gateway_status,
                        )
                        return (
                          <tr
                            key={`${row?.payment_log_id ?? row?.pa_id ?? index}`}
                            className={!row?.payment_log_id ? 'bg-sky-50/40' : undefined}
                          >
                            <td className="px-3 py-2 break-words">
                              {display(row?.payment_type)}
                            </td>
                            <td className="px-3 py-2 break-words">
                              {display(row?.payment_source)}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {formatAmount(paymentLogBaseFee(row))}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {formatAmount(paymentLogServiceFee(row))}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {formatAmount(toNumber(row?.payment_amount))}
                            </td>
                            <td className="px-3 py-2">
                              {display(row?.payment_log_id)}
                            </td>
                            <td className="px-3 py-2">
                              {formatDetailDateTime(row?.created_at)}
                            </td>
                            <td className="px-3 py-2">
                              {renderStatusBadge(
                                row?.payment_status ||
                                (row as Record<string, unknown>)?.chargeback_status ||
                                (row as Record<string, unknown>)?.chargeback_type
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <span className="inline-flex min-w-0 items-center gap-1">
                                <span>{display(gateway.display)}</span>
                                {row?.gateway_status ? (
                                  <SectionInfoIcon
                                    align="start"
                                    maxWidth={320}
                                    iconSize={14}
                                    aria-label="Gateway status details"
                                    className="shrink-0"
                                    clampPanelHeight={false}
                                    contentClassName="px-3 py-2 text-xs text-slate-700 whitespace-pre-wrap"
                                    content={gateway.description || row.gateway_status}
                                  />
                                ) : null}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              <span className="inline-flex min-w-0 items-center gap-1">
                                <span className="truncate">{display(row?.paid_by)}</span>
                                {row?.transaction_notes ? (
                                  <SectionInfoIcon
                                    align="start"
                                    maxWidth={320}
                                    iconSize={14}
                                    aria-label="Transaction notes"
                                    className="shrink-0"
                                    clampPanelHeight={false}
                                    contentClassName="px-3 py-2 text-xs text-slate-700 whitespace-pre-wrap"
                                    content={String(row.transaction_notes)}
                                  />
                                ) : null}
                              </span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}

            {showChargebackHistory ? (
              <HistoryTable
                title="Chargeback History"
                icon={<RotateCcw className="h-4 w-4" aria-hidden />}
                columns={[
                  'Progress',
                  'Notes',
                  'Cancel Application',
                  'Gen Chargeback Packet',
                  'Updated By',
                  'Updated At',
                  'Payment Log Id',
                ]}
                rows={adjustmentHistory.map((row) => [
                  display(row?.chargeback_type),
                  display(row?.executive_notes),
                  ['CHARGEBACK_CANCEL_CERTIFICATE', 'CANCEL_CERTIFICATE'].includes(
                    String(row?.adjustment_subtype),
                  )
                    ? 'YES'
                    : 'NO',
                  String(row?.gen_chargeback_pack) === '1' ? 'YES' : 'NO',
                  display(row?.username),
                  formatDetailDateTime(row?.executive_date),
                  display(row?.fk_payment_log_id),
                ])}
              />
            ) : null}

            {showBalanceAdjustmentHistory ? (
              <HistoryTable
                title="Balance Adjustment History"
                icon={<ShieldAlert className="h-4 w-4" aria-hidden />}
                columns={[
                  'Progress',
                  'Notes',
                  'Action',
                  'Updated By',
                  'Updated At',
                  'Payment Log Id',
                ]}
                rows={adjustmentHistory.map((row) => [
                  display(row?.chargeback_type),
                  display(row?.executive_notes),
                  display(row?.adjustment_subtype),
                  display(row?.username),
                  formatDetailDateTime(row?.executive_date),
                  display(row?.history_id),
                ])}
              />
            ) : null}

            {showNotesHistory ? (
              <HistoryTable
                title="Notes History"
                icon={<FileText className="h-4 w-4" aria-hidden />}
                columns={['Notes', 'Updated By', 'Updated At']}
                rows={notesHistory.map((row) => [
                  display(row?.notes),
                  display(row?.added_by),
                  formatDetailDateTime(row?.created_at),
                ])}
              />
            ) : null}
          </div>
        ) : (
          <p className="text-center text-slate-500">Payment log not found.</p>
        )}
      </PageShell>
    </div>
  );
}

function renderStatusBadge(status: unknown) {
  const statusStr = display(status);
  const lower = statusStr.toLowerCase();

  if (lower.includes('pending') || lower.includes('fail') || lower.includes('error')) {
    return (
      <span className={styles.statusBadgeWarning}>
        <Clock className="w-3 h-3 mr-1.5" />
        {titleCaseStatus(status)}
      </span>
    );
  }
  if (lower.includes('complete') || lower.includes('success')) {
    return (
      <span className={styles.statusBadgeSuccess}>
        {titleCaseStatus(status)}
      </span>
    );
  }
  return <span className={styles.statusBadgeDefault}>{titleCaseStatus(status)}</span>;
}

function InfoRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={styles.infoRow}>
      <span className={styles.infoLabel}>{label}</span>
      <span className={styles.infoValue}>{value || display(value)}</span>
    </div>
  )
}

function HistoryTable({
  title,
  icon,
  columns,
  rows,
}: {
  title: string
  icon?: ReactNode
  columns: string[]
  rows: ReactNode[][]
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-2.5 border-b border-slate-100 px-5 py-3">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50 text-[#1597ff]">
          {icon ?? <History className="h-4 w-4" aria-hidden />}
        </div>
        <h2 className="text-sm font-semibold text-slate-800">{title}</h2>
      </div>
      <div className="overflow-x-auto p-3 sm:p-4">
        <table className="w-full min-w-[720px] text-left text-xs text-slate-700">
          <thead className="border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
            <tr>
              {columns.map((column) => (
                <th key={column} className="px-3 py-2">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((cells, rowIndex) => (
              <tr key={rowIndex}>
                {cells.map((cell, cellIndex) => (
                  <td key={columns[cellIndex] ?? cellIndex} className="px-3 py-2 break-words">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function MultilineValue({
  lines,
  strongFirstLine = false,
}: {
  lines: string[]
  strongFirstLine?: boolean
}) {
  if (!lines.length) {
    return <p className="text-sm text-slate-800">-</p>
  }

  return (
    <div className="space-y-1 text-sm text-slate-800">
      {lines.map((line, index) => (
        <p
          key={`${line}-${index}`}
          className={strongFirstLine && index === 0 ? 'font-semibold text-slate-900' : undefined}
        >
          {line}
        </p>
      ))}
    </div>
  )
}
